import { DateTime } from '@ksyos/date-time';
import { Text } from '@ksyos/design-system';
import { connect } from 'react-redux';
import type { IRequest } from '../../../common/interfaces/IRequest';
import type { IReduxState } from '../../configureStore';
import { ProcedureStatus } from '../../../common/enums/ProcedureStatus';
import { ServiceCode } from '../../../common/enums/ServiceCode';
import { isPendingRequestForService } from '../../utils/isPendingRequestForService';
import { CardTheme, FixedCard } from '../elements/molecules/card/Card';

interface IRequestStatusCardProps {
    request: IRequest;
}

interface IRequestStatusCardReduxProps {
    careProviderName: string | null;
}

const mapStateToProps = ({ authentication }: IReduxState): IRequestStatusCardReduxProps => ({
    careProviderName: authentication.user?.careProviderName || null,
});

function RequestStatusCardWrapper(props: IRequestStatusCardProps & IRequestStatusCardReduxProps) {
    let card;
    const serviceCode = props.request.service.code;
    if (isPendingRequestForService(props.request, serviceCode)) {
        card = getPendingRequestCardForService();
    } else if (props.request.procedure?.status === ProcedureStatus.completed) {
        card = getCompletedRequestCardForService();
    }

    if (!card) {
        throw new Error(`Invalid status for service ${serviceCode}`);
    }

    return card;

    function getPendingRequestCardForService() {
        const cardProps = getRequestCardPropsForService();

        return (
            <FixedCard
                {...cardProps}
                body={`Ontvangen op ${
                    props.request.dateSent ? DateTime.from(props.request.dateSent).format('DD-MM-YYYY') : ''
                }`}
                linksText={['Beantwoord de vragen']}
            >
                <Text>Je huisarts heeft wat vragen voor je.</Text>
            </FixedCard>
        );
    }

    function getCompletedRequestCardForService() {
        const { theme } = getRequestCardPropsForService();

        return (
            <FixedCard
                body={`Verstuurd op ${
                    props.request.procedure?.dateSent
                        ? DateTime.from(props.request.procedure.dateSent).format('DD-MM-YYYY')
                        : ''
                }`}
                cardTitle="Je antwoorden zijn verstuurd"
                theme={theme}
            >
                <Text>
                    Je huisarts zal per telefoon een reactie geven als jouw situatie is bekeken. Houd je telefoon daarom
                    bij de hand.
                </Text>
            </FixedCard>
        );
    }

    function getRequestCardPropsForService() {
        let theme: CardTheme = CardTheme.empty;
        let cardTitle = '';
        let linkTo = '';
        switch (serviceCode) {
            case ServiceCode.DermaOth:
                theme = CardTheme.white;
                cardTitle = 'Het dermatologisch thuisconsult';
                linkTo = '/dermaOth/request';
                break;
            case ServiceCode.GTO:
                theme = CardTheme.white;
                cardTitle = `Vragen van ${
                    props.careProviderName ? props.careProviderName + ' (huisarts)' : 'huisarts'
                }`;
                linkTo = '/ghc/request';
                break;
            default:
                break;
        }

        return { cardTitle, linksTo: [linkTo], theme };
    }
}

export const RequestStatusCard = connect(mapStateToProps)(RequestStatusCardWrapper);
